import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0308 from "../lesson-08/lesson";
import lesson0309 from "../lesson-09/lesson";
import lesson0310 from "../lesson-10/lesson";
import lesson0311 from "../lesson-11/lesson";
const linkLesson0308 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0308.id);
const linkLesson0309 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0309.id);
const linkLesson0310 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0310.id);
const linkLesson0311 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0311.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>This lesson presents an overview on acoustic simulation methods. Their objective is to predict the acoustic response of systems subjected to dynamic loads. It can be, for example, the vibratory response of a car, the prediction of noise radiated by an aircraft or the acoustic field generated by a loudspeaker in a room. For simple problems, it is sometimes possible to determine exact or analytical solutions but in most of the practical application cases, the complexity of the geometry and/or boundary conditions prevents such solutions from being directly obtained. Simulations are then carried out through numerical methods whose key idea is to find a discrete approximation of the solution of the problem. The number of unknowns of this approximation can be quite large (nowadays, it can go up to several billions) but hopefully they can be determined by computers even if it sometimes requires significant resources.<br class=newline>
<p>The variety of numerical methods is quite high, even in the range of acoustic simulations. They differ by their discretisation strategy, which depends on the topology of the problem. One key parameter is the frequency range of the simulation especially because the physical behaviour is modelled differently for low and high frequencies. For a given method, the accuracy of the approximation is controlled by some parameters that should be chosen by the end-user (size of the elements, number of rays ...) and which requires a real expertise.<br class=newline>
<p>This lesson is divided in two parts. First, a focus is done on discretisation and then a selection of the main methods used for acoustic simulations is presented.
<h3 class=sectionHead>Topics on discretisation</h3>

<p>
   In order to present the underlying ideas beyond discretization, let’s consider the case of numerical differentiation. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   be a smooth function. Its derivative
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   at point
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   is defined as
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msup>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munder class=msub>
               <mrow>
                  <mi class=qopname>lim</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mi>h</mi>
                  <mo class=MathClass-rel>→</mo>
                  <mn>0</mn>
               </mrow>
            </munder>
            <mfrac>
               <mrow>
                  <mi>f</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>h</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>f</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>h</mi>
               </mrow>
            </mfrac>

         </math>
      <td class=eq-no>(1)
</table>
<p>
   This definition is based on the limit operator which is a formal mathematical tool. A first solution to approximate this derivative consists in choosing a given value for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   (quite small) and to approximate the derivative as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>x</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>f</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>h</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mi>f</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>x</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>h</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Geometrically, this corresponds to the slope of the line that passes by the two points of the function associated to abscissa
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>h</mi>
   </math>
   , as depicted in <a href="#" data-img-alt="Figure 1. Approximation of the derivative of a function by finite-differences." data-img-src=${config.ASSETS_URL}/03/01/theory/figure-1.png>Figure 1</a>.
 This strategy is called the linear Finite-Difference (FD) approximation of the derivative. It can be easily understood that the smaller
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   is, the better is the approximation. Another strategy (called quadratic FD) is based on three points. It first determines the interpolation polynomial, that passes by the three points and approximate the derivative at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   by the one of the polynomial.<br class=newline>
   
   <figure class=figure>
   <img src="${config.ASSETS_URL}/03/01/theory/figure-1.svg" alt="Figure 1. Approximation of the derivative of a function by finite-differences.">
   <figcaption class=caption>Figure 1. Approximation of the derivative of a function by finite-differences.</figcaption>
</figure>

<p>
   The strategy of a simulation method is then to first do an <em><span class=cmti-10>a-priori</span></em> on the solution (polynomial approximation in the case of FD) and then adapt the parameters (value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   for FD) of the simulation to control the quality of the approximation. For a given method, the computational cost will be higher if you want to increase the precision (if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
   </math>
   is smaller, you increase the precision but in counterpart, you will have more points, so more calculations !). Nevertheless, keep in mind that the objective is not always to have a very accurate model: it is not necessary to have a precision of 12 digits if your input physical parameters are known with a precision of several percents.<br class=newline>
<h3 class=sectionHead>Acoustic simulation models</h3>
<p>The need of an <em><span class=cmti-10>a-priori</span></em> on the solution was highlighted in the previous example. In the case of an acoustic model, it should be combined with another assumption, which is relative to the physics of the problem. Indeed, it can be easily understood that for room or environmental problems, viscothermal and relativistic effects, that increase drastically the complexity of the model, can easily be neglected in all cases. More interesting is the behaviour of acoustic waves. When the wavelength of the acoustic waves is small compared to the dimensions of the room, all of the wave properties of the solution can be neglected. This splits the methods in two main categories [3, 2]: Wave-based methods, for low-frequency simulations, which corresponds to numerical methods in which the full wave equation is solved [1] and ray-energy methods, for higher frequency simulations which are based on simplification of the wave equation.
<h4 class=subsectionHead>Low frequency methods</h4>
<p>Wave-based methods are also called element-methods. Indeed, these techniques rely on a discretisation of space or of the boundary of the domain. They require a mesh which is a partition of the domain in smaller elements. All these methods have also the common point of a local polynomial approximation so that their accuracy can be controlled by either a mesh refinement or an increase of the polynomial order. All these techniques share the same drawback: their computational cost increases drastically when the wavelength becomes small compared to the typical size of the mesh, typically in the high frequency range. Three methods are particularly popular and gather most of the low-frequency acoustic simulations:
<ul class=itemize1>
   <li class=itemize>The Finite-Difference method in Time Domain (FDTD) is based on a discretisation of the wave equation based on Taylor’s formula at the nodes of a volumic mesh. As it requires the approximation of partial derivatives the mesh needs to be structured (i.e. based on a grid).
   <li class=itemize>The Finite-Element Method (FEM) is a polyvalent and robust method whose range of application is wider than just acoustic simulations. This method does not discretise the wave equation but a weak-form of Helmholtz equation. The mesh is a partition of the domain into elements which leads to a polynomial by piece approximation of the solution of the weak form. Contrary to FDTD, FEM does not requires the mesh to be structured.
   <li class=itemize>The Boundary Element Method (BEM) is also based on the discretisation an unstructured mesh but this one corresponds to the boundary of the domain on which the strategy of approximation is similar to the one of the FEM. An integral equation is then used to determine the pressure inside the domain.
</ul>
<p>FDTD and FEM are based on a volumic mesh while BEM relies on a surface mesh so that the latter should be preferred in terms of global number of degrees of freedom. In addition, as FEM is associated to an unstructured mesh, it should be privileged to FDTD when the geometry of the problem is complex or contains lots of small details. On the other hand one key advantage of FDTD is that it is natively a time domain method so that obtaining the temporal response of the room is more straightforward than with the other two methods. Wave based methods are the object of <a href="${linkLesson0311}" target="_blank">Lesson 3.11</a>.
<h4 class=subsectionHead>High frequency methods</h4>
<p>Geometrical methods are based on the assumption that the wave can be modelled as a particle and thereby neglect diffraction and interference phenomena. The advantage is that computational effort is way smaller then wave-based models. All this methods are based on so-called ”sound particles” which propagate in the space. The difference between the methods is the way of simulating this propagation. In this way each geometrical method has its advantages and disadvantages. Every method will be discussed in detail in the following lessons. For this reason, we will just take list the different methods and give the link to the lessons in which they will be presented in the following of this course.
<ul class=itemize1>
   <li class=itemize><a href="${linkLesson0308}" target="_blank">Image Source Method (ISM), Lesson 3.8</a>
   <li class=itemize><a href="${linkLesson0309}" target="_blank">Ray tracing methods, Lesson 3.9</a>
   <li class=itemize><a href="${linkLesson0310}" target="_blank">Radiosity method, Lesson 3.10</a>
</ul>
<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>BEM
  <dd>Boundary Element Method</dd>
  <dt>FD
  <dd>Finite-Difference
  <dt>FDTD
  <dd>Finite-Difference Time-Domain 
  <dt>FEM
  <dd>Finite Element Method
  <dt>ISM
  <dd>Image Source Method
</dl>

<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt>[1]
  <dd>N. Atalla and F. Sgard. Finite Element and Boundary Methods in Structural Acoustics and Vibration. 2017.
  <dt>[2]
  <dd>L. Savioja and U. P. Svensson. Overview of geometrical room acoustic modeling techniques. J. Acoust. Soc. Am., 138(2):708–730, 2015.
  <dt>[3]
  <dd>S. Siltanen, T. Lokki, and L. Savioja. Rays or Waves? Understanding the Strengths and Weaknesses of Computational Room Acoustics Modeling Techniques. In roceedings of the International Symposium on Room Acoustics, ISRA 2010, 2010.
</dl>`;

export default theory;
